import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';
import { construct, get } from 'radash';

import { Component } from '../../types/Component';
import { ComponentFileType } from '../../types/ComponentFileType';
import { ComponentSection } from '../../types';
import { ComponentImageType } from '../../validators/component-sections/images';
import type { Converter } from '../../validators/components/Converter';

const getSectionProgressValues = (component: Partial<Component>, section: ComponentSection) => {
    const sectionValues = getSectionValues(component, section);

    const metadataValues =
        section === ComponentSection.GENERAL ? component.metadata : get(component, `metadata.${section}`);

    const keysToRemove = Object.entries(SyncEngineHelpers.crush(metadataValues))
        .filter(([key, value]) => key.endsWith('display') && value !== '')
        .map(([key]) => key.replace('.display', ''));

    const valuesWithoutMetadataKeys = construct(
        Object.fromEntries(
            Object.entries(SyncEngineHelpers.crush(sectionValues))
                .filter(([valueKey]) => !keysToRemove.some((metaKey) => valueKey.startsWith(metaKey)))
                .map(([key, value]) => [key, value === undefined ? null : value]),
        ),
    );

    return valuesWithoutMetadataKeys;
};

const getSectionValues = (component: Partial<Component>, section: ComponentSection) => {
    switch (section) {
        case ComponentSection.GENERAL:
            return component;
        case ComponentSection.FILES:
            return getDatasheets(get(component, section));
        case ComponentSection.IMAGES:
            return getThumbnailFile(get(component, section));
        case ComponentSection.COMPATIBLE_WITH:
            return getCompatibleWithValues(component);
        case ComponentSection.STANDARDS:
            return { standards: component.standards };
        case ComponentSection.ELECTRICAL:
            return component.type === 'converter' && component.electrical
                ? getConverterElectricalValues(component.electrical)
                : (get(component, section) as object);
        default:
            return get(component, section) as object;
    }
};

const getDatasheets = (files: Component['files']) => ({
    hasDatasheet: files?.some((file) => file.file && file.type === ComponentFileType.DATASHEET),
});

const getThumbnailFile = (images: Component['images']) => ({
    hasThumbnail: images?.some((image) => image.file && image.type === ComponentImageType.THUMBNAIL),
});

const getConverterElectricalValues = ({ efficiency, ...electrical }: Converter['electrical']) => ({
    ...electrical,
    hasEfficiency: Boolean(efficiency?.curves?.length),
});

const getCompatibleWithValues = (component: Partial<Component>) => ({
    compatibleWith: [...(component.compatibleWith || []), ...(component.compatibleWithPlaceholders || [])],
});

export { getSectionProgressValues };
